import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { confirmForgotPassword, setAuthError } from '../store/slices/authSlice';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MyTextField from '../components/MyTextField';
import MySnackbar from '../components/MySnackbar';
import LoadingComponent from '../components/LoadingComponent';
import s10Logo from '../assets/images/s10Logo.png';

const ResetPassword = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, error } = useSelector((state: RootState) => state.auth);
    
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword] = useState(false);
    const [codeError, setCodeError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmError, setConfirmError] = useState('');

    const email = location.state?.email || '';

    const validateForm = (): boolean => {
        let isValid = true;

        if (!code.trim()) {
            setCodeError('El código es requerido');
            isValid = false;
        } else {
            setCodeError('');
        }

        if (!newPassword) {
            setPasswordError('La nueva contraseña es requerida');
            isValid = false;
        } else if (newPassword.length < 6) {
            setPasswordError('La contraseña debe tener al menos 6 caracteres');
            isValid = false;
        } else {
            setPasswordError('');
        }

        if (newPassword !== confirmPassword) {
            setConfirmError('Las contraseñas no coinciden');
            isValid = false;
        } else {
            setConfirmError('');
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        const result = await dispatch(confirmForgotPassword({
            email,
            code,
            newPassword,
        }));

        if (confirmForgotPassword.fulfilled.match(result)) {
            navigate('/login', { state: { message: 'Contraseña restablecida exitosamente' } });
        }
    };

  return (
    <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh', backgroundColor: theme.s10palette?.S10Blueblue50 }}>
        <MySnackbar
            open={!!error}
            autoHideDuration={6000}
            msg={error || ''}
            severity="error"
            onClose={() => dispatch(setAuthError(''))}
        />

        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
            <Grid container direction="column" alignItems="center" component="form" onSubmit={handleSubmit} spacing={2}>
                <Grid item>
                    <img src={s10Logo} alt="S10" style={{ width: '8.75rem' }} />
                </Grid>

                <Typography variant="h5" align="center" gutterBottom>
                    Restablecer contraseña
                </Typography>

                <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 2 }}>
                    Ingresa el código que recibiste por email y tu nueva contraseña.
                </Typography>

                <Divider sx={{ width: '100%', my: 2 }} />

                <MyTextField
                    label="Email"
                    type="email"
                    value={email}
                    disabled
                    fullWidth
                />

                <MyTextField
                    label="Código de verificación"
                    type="text"
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                    error={!!codeError}
                    helperText={codeError}
                    fullWidth
                />

                <MyTextField
                    label="Nueva contraseña"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                    error={!!passwordError}
                    helperText={passwordError}
                    fullWidth
                />

                <MyTextField
                    label="Confirmar contraseña"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    error={!!confirmError}
                    helperText={confirmError}
                    fullWidth
                />

                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate('/forgot-password')}
                        sx={{ borderColor: theme.s10palette?.S10Greygrey300 }}
                    >
                        Volver
                    </Button>
                    </Grid>
                    <Grid item xs={6}>
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={isLoading}
                        sx={{
                        backgroundColor: theme.s10palette?.S10Blueblue600,
                        '&:hover': { backgroundColor: theme.s10palette?.S10Blueblue700 },
                        }}
                    >
                        Confirmar
                    </Button>
                    </Grid>
                </Grid>

                <Grid item sx={{ mt: 2 }}>
                    <Typography variant="body2">
                    <Link to="/login" style={{ color: theme.s10palette?.S10Blueblue600, textDecoration: 'none' }}>
                        Volver al inicio de sesión
                    </Link>
                    </Typography>
                </Grid>
            </Grid>
        </Paper>

        {isLoading && <LoadingComponent loadingText="Restableciendo contraseña..." />}
    </Grid>
  );
};

export default ResetPassword;